/**
 * The interface language: the app's `I18n` (Assets/Scripts/I18n.cs) and its `TranslationModel`
 * (Assets/Scripts/GUI/Translation/TranslationModel.cs) for the web.
 *
 * Each language is one file of keys and values, `src/i18n/langs/<code>.json`, as the app keeps one
 * `Resources/Langs/<code>.xml` per language. Keys follow the app: `#PascalCase`, or the English words
 * themselves for a few short ones (`Cancel`, `cm`); a key the app already has keeps the app's name and
 * text, so its other languages can be taken from the app. A value takes `{0}`, `{1}`… for arguments,
 * and `|` for a line break, as in the app. The catalog's titles, which the app keeps in the same files, are
 * generated beside them into `src/i18n/langs/catalog/<code>.json`. How to add text or a language: docs/i18n.md.
 *
 * As in the app:
 * - the language follows the system — here the browser's languages — through the app's table of
 *   language codes (`GetLanguageCodeUI`); one the web has no file for falls back to English;
 * - a key missing in the language is taken from English, and a key missing there too is shown as is;
 * - a count picks the key's `One`, `Few` or `Many` form by the app's rules (`PaywallTrialText.PluralSuffix`).
 *
 * Unlike the app, arguments are not translated themselves: a caller translates what needs it. Numbers
 * among them are written with the culture's decimal separator.
 *
 * The language is chosen once, when the page loads, before the interface renders; nothing translates
 * at module load, so tests and `?lang=` can set it first. `?lang=ru` (or `en`) overrides the browser
 * for this browser until `?lang=auto`, as the editor's `DebugCultureName` does in the app.
 */
import en from './langs/en.json';
import ru from './langs/ru.json';
// The catalog's and the material sheet's titles, from the app's Langs (tools/build-catalog-langs.ts).
import catalogEn from './langs/catalog/en.json';
import catalogRu from './langs/catalog/ru.json';

export type TranslationKey = keyof typeof en;
type PluralPrefix<K> = K extends `${infer P}One` ? P : never;
/** A key that has `One`, `Few` and `Many` forms, named without the suffix. */
export type PluralKey = PluralPrefix<TranslationKey>;
export type LanguageCode = 'en' | 'ru';
type Arg = string | number;

const DEFAULT_LANGUAGE: LanguageCode = 'en';
const LANGS: Record<LanguageCode, Record<string, string>> = { en: { ...catalogEn, ...en }, ru: { ...catalogRu, ...ru } };
/** The culture a language is written in when the browser names none of its own (the app's FixCultureName). */
const DEFAULT_CULTURES: Record<LanguageCode, string> = { en: 'en-US', ru: 'ru-RU' };
export const LANGUAGES = Object.keys(LANGS) as LanguageCode[];
const OVERRIDE_KEY = 'hp.language';

/**
 * The app's `I18n.GetLanguageCodeUI`: which of the app's languages a browser language tag is shown in.
 * The answer may be a language the web has no file for yet; `resolveLanguage` falls back to English.
 */
export function languageCodeUI(tag: string): string {
  const lang = tag.trim().toLowerCase().replace(/_/g, '-');
  const parts = lang.split('-');
  const candidates = parts[0] !== lang ? [lang, parts[0]] : [lang];
  // The app reads the country from the culture for Chinese; here it is the tag's last part.
  const country = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  for (const name of candidates) {
    switch (name) {
      case 'hi': case 'uz-latn': return 'en';
      case 'ru': case 'be': case 'uz': return 'ru';
      case 'ar': case 'ars': return 'ar';
      case 'id': case 'in': return 'id';
      case 'cs': case 'da': case 'de': case 'en': case 'bg': case 'fi': case 'sk': case 'hr': case 'vi':
      case 'fr': case 'hu': case 'it': case 'ro': case 'sv': case 'zh-hans': case 'zh-hant': return name;
      case 'es': case 'es-hans': case 'es-hant': return 'es';
      case 'he': case 'iw': case 'ji': case 'yi': return 'he';
      case 'ja': case 'ja-hans': case 'ja-hant': return 'ja';
      case 'ko': case 'ko-kore': case 'ko-hans': case 'ko-hant': return 'ko';
      case 'nb': case 'nn': return 'nb';
      case 'nl': case 'nl-hans': case 'nl-hant': return 'nl';
      case 'pl': case 'pl-hans': case 'pl-hant': return 'pl';
      case 'pt': case 'pt-hans': case 'pt-hant': return 'pt';
      case 'th': case 'th-hans': case 'th-hant': return 'th';
      case 'tr': return 'tr';
      case 'uk': return 'uk';
      case 'zh': return country === 'TW' || country === 'MO' || country === 'HK' ? 'zh-hant' : 'zh-hans';
      case 'zh-mo': case 'zh-hk': return 'zh-hant';
    }
  }
  return 'en';
}

const isLanguage = (code: string): code is LanguageCode => code in LANGS;

/** The first of the browser's languages the web is translated into, and the culture its numbers and dates follow. */
export function resolveLanguage(tags: readonly string[]): { language: LanguageCode; culture: string } {
  for (const tag of tags) {
    const code = languageCodeUI(tag);
    if (isLanguage(code)) return { language: code, culture: cultureOf(tag, code) };
  }
  return { language: DEFAULT_LANGUAGE, culture: DEFAULT_CULTURES[DEFAULT_LANGUAGE] };
}

/** The tag itself when it names a region (`en-GB`), else the language's own culture. */
function cultureOf(tag: string, language: LanguageCode): string {
  try {
    const [canonical] = Intl.getCanonicalLocales(tag);
    if (canonical && canonical.includes('-')) return canonical;
  } catch { /* not a tag Intl knows */ }
  return DEFAULT_CULTURES[language];
}

let current: LanguageCode = DEFAULT_LANGUAGE;
let culture = DEFAULT_CULTURES[DEFAULT_LANGUAGE];
let decimalSeparator = '.';

/** The language the interface is shown in (the app's `LanguageCodeUI`). */
export const language = (): LanguageCode => current;
/** The culture numbers and dates are written in, for `Intl` and `toLocale…` (the app's `CultureName`). */
export const cultureName = (): string => culture;

/**
 * Shows the interface in `code` — a language code or any browser tag; one the web has no file for
 * means English. Without a code, the browser's languages decide. Tests call it before anything
 * renders; the page calls it once at load.
 */
export function setLanguage(code?: string | null, cultureTag?: string): LanguageCode {
  const resolved = code ? resolveLanguage([code]) : resolveLanguage(browserLanguages());
  current = resolved.language;
  culture = cultureTag ?? (code && !code.includes('-') ? DEFAULT_CULTURES[current] : resolved.culture);
  try {
    decimalSeparator = new Intl.NumberFormat(culture).formatToParts(1.5).find(part => part.type === 'decimal')?.value ?? '.';
  } catch { decimalSeparator = '.'; }
  return current;
}

function browserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? [...navigator.languages] : navigator.language ? [navigator.language] : [];
}

/**
 * The language at page load: `?lang=` when given (kept for this browser; `?lang=auto` forgets it),
 * else the one kept from an earlier `?lang=`, else the browser's.
 */
export function initLanguage(): LanguageCode {
  let override: string | null = null;
  try {
    const asked = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('lang') : null;
    if (asked === 'auto' || asked === '') localStorage.removeItem(OVERRIDE_KEY);
    else if (asked) localStorage.setItem(OVERRIDE_KEY, asked);
    override = localStorage.getItem(OVERRIDE_KEY);
  } catch { /* storage blocked: the browser decides */ }
  setLanguage(override);
  if (typeof document !== 'undefined') document.documentElement.lang = culture;
  return current;
}

const warned = new Set<string>();

/**
 * The text of `key` in the interface language (the app's `I18n.Translate`). For keys that come with
 * data — a catalog title, a room type — whose name the compiler cannot check; `t` is for the rest.
 */
export function translate(key: string, ...args: Arg[]): string {
  return translateTo(current, key, ...args);
}

/** The text of `key` in one of the editor's languages, whichever the interface is in (the app's `I18n.TranslateTo`). */
export function translateTo(code: LanguageCode, key: string, ...args: Arg[]): string {
  if (!key) return key;
  let text = LANGS[code][key] ?? LANGS[DEFAULT_LANGUAGE][key];
  if (text === undefined) {
    if (key.startsWith('#') && !warned.has(key)) { warned.add(key); console.warn(`[i18n] no translation for ${key}`); }
    text = key;
  }
  // The line break is the text's own: a `|` in an argument — a project's name — stays as it is.
  text = text.replace(/\|/g, '\n');
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    text = text.split(`{${i}}`).join(typeof arg === 'number' ? decimal(String(arg)) : arg);
  }
  return text;
}

/** The text of a key the language files have; the compiler checks the name. */
export const t = (key: TranslationKey, ...args: Arg[]): string => translate(key, ...args);

/**
 * Whether `value` is the text of `key` in any of the editor's languages: a default name or a status that
 * was saved — into a project, a chat — while the interface was in another language.
 */
export const isTextOf = (key: TranslationKey, value: string | undefined): boolean =>
  value !== undefined && LANGUAGES.some(code => translateTo(code, key) === value);

/** Whether `key` is in the language files (the app's `I18n.KeyExists`). */
export const hasKey = (key: string): boolean => !!key && (key in LANGS[current] || key in LANGS[DEFAULT_LANGUAGE]);

/**
 * The app's `PaywallTrialText.PluralSuffix`: which form a whole count takes. A language without a
 * `Few` form keeps the `Many` text under `Few` too, so every plural key has all three.
 */
export function pluralSuffix(lang: string, count: number): 'One' | 'Few' | 'Many' {
  const n = Math.abs(Math.trunc(count)), mod10 = n % 10, mod100 = n % 100;
  const fewEnding = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14);
  switch (lang) {
    case 'ru': case 'uk': case 'hr': return mod10 === 1 && mod100 !== 11 ? 'One' : fewEnding ? 'Few' : 'Many';
    case 'pl': return n === 1 ? 'One' : fewEnding ? 'Few' : 'Many';
    case 'cs': case 'sk': return n === 1 ? 'One' : n >= 2 && n <= 4 ? 'Few' : 'Many';
    case 'ro': return n === 1 ? 'One' : n === 0 || (mod100 >= 1 && mod100 <= 19) ? 'Few' : 'Many';
    case 'ar': return n === 1 ? 'One' : mod100 >= 2 && mod100 <= 10 ? 'Few' : 'Many';
    case 'fr': case 'pt': return n <= 1 ? 'One' : 'Many';
    default: return n === 1 ? 'One' : 'Many';
  }
}

/** A count with its word: `{0}` is the count, `{1}`… the further arguments. */
export const plural = (key: PluralKey, count: number, ...args: Arg[]): string =>
  translate(key + pluralSuffix(current, count), count, ...args);

/** A number already written with a dot (`toFixed`, `String`) in the culture's decimal separator. */
export const decimal = (text: string): string => decimalSeparator === '.' ? text : text.replace('.', decimalSeparator);

/** `value.toFixed(digits)` with the culture's decimal separator. */
export const fixed = (value: number, digits: number): string => decimal(value.toFixed(digits));

/** A number in the culture's writing — digits grouped, at most `maxDigits` after the separator. */
export const formatNumber = (value: number, maxDigits = 2, options: Intl.NumberFormatOptions = {}): string =>
  value.toLocaleString(culture, { maximumFractionDigits: maxDigits, ...options });

setLanguage();
